import React, { useEffect, useMemo, useState } from "react";
import TitleButtons from "../../../components/TitleButtons";
import TextField from "../../../components/TextField";
import TextFieldRow from "../../../components/TextFieldRow";
import Button from "../../../components/Button";
import MultiSelectField from "../../../components/MultiSelectField";

import { useMsal } from "@azure/msal-react";
import { loginRequestHavoxCases } from "../../../integrations/auth/msalConfigHavoxCases";
import { createCaseFolderInLab } from "../../../integrations/onedrive";

import { jsPDF } from "jspdf";

const API_REGISTER =
  "https://api.logvi-havox.com/departamentos/laboratorio/gestao-de-case/registrar-case";

const API_EMPRESAS_NOME =
  "https://api.logvi-havox.com/geral/empresa-clientes-nome";

const API_EMPRESAS_COMPLETO =
  "https://api.logvi-havox.com/geral/empresa-clientes-completo";

const API_SOLICITANTES = "https://api.logvi-havox.com/geral/solicitantes";

const TIPO_ATENDIMENTO_OPTIONS = ["MANUTENCAO", "DOCAGEM"];
const ENABLE_ONEDRIVE_INTEGRATION = false;

const toUpper = (setter: (v: string) => void) => (v: string) =>
  setter((v ?? "").toUpperCase());

function sanitizeCaseRef(value: string): string {
  const raw = String(value ?? "")
    .toUpperCase()
    .trim();

  if (!raw) return "";

  // Mantém apenas a primeira parte quando houver concatenação do tipo:
  // "HVX-26-001 - EMPRESA - EQUIPAMENTO"
  const firstPart = raw.split(" - ")[0]?.trim() ?? "";

  return firstPart;
}

type NoticeType = "success" | "error" | "notify";
type Notice = { type: NoticeType; text: string };

type EmpresaOpt = { id: number | null; nome: string };
type SolicitanteOpt = { id: number | null; nome: string };
type SelectedSolic = { id: number; label: string };

async function fetchJson(url: string) {
  const res = await fetch(url, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
    cache: "no-store",
  });

  const rawText = await res.text().catch(() => "");
  let data: any = null;

  try {
    data = rawText ? JSON.parse(rawText) : null;
  } catch {
    data = {
      raw: rawText,
      parse_error: true,
    };
  }

  return { res, data, rawText };
}

function asNumber(v: any): number | null {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : null;
}

function pickNomeFromObj(o: any): string | null {
  const candidates = [o?.nome, o?.razao_social, o?.fantasia, o?.empresa_nome];
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) return c.trim();
  }
  return null;
}

function pickIdFromObj(o: any): number | null {
  const candidates = [
    o?.id,
    o?.empresa_cliente_id,
    o?.empresaId,
    o?.empresa_id,
  ];
  for (const c of candidates) {
    const n = asNumber(c);
    if (n != null) return n;
  }
  return null;
}

function todayBR(): string {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = String(d.getFullYear());
  return `${dd}/${mm}/${yyyy}`;
}

function maskDateBR(input: string): string {
  const digits = String(input ?? "")
    .replace(/\D+/g, "")
    .slice(0, 8);

  const dd = digits.slice(0, 2);
  const mm = digits.slice(2, 4);
  const yyyy = digits.slice(4, 8);

  if (digits.length <= 2) return dd;
  if (digits.length <= 4) return `${dd}/${mm}`;
  return `${dd}/${mm}/${yyyy}`;
}

function safeText(v: any) {
  const s = v === null || v === undefined ? "" : String(v).trim();
  return s ? s : "—";
}

function nowBRDateTime(): string {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
}

function generateCasePdfBlob(args: {
  case_ref: string;
  data_recebimento: string | null;
  nf: string | null;
  tipo_atendimento: string | null;
  equipamento_nome: string;
  descricao_problema: string;
  empresa_cliente_nome: string | null;
  embarcacao_nome: string | null;
  solicitante_nome: string | null;
  prioridade?: number | null;
}) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });

  const pageW = 210;
  const margin = 14;
  const contentW = pageW - margin * 2;

  const footerY = 290;
  const footerGapTop = 10;
  const maxContentBottom = footerY - footerGapTop;

  doc.setFillColor(18, 66, 128);
  doc.rect(0, 0, pageW, 24, "F");

  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text("LABORATÓRIO — FICHA DO CASE", margin, 14);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(`Gerado em: ${nowBRDateTime()}`, pageW - margin, 14, {
    align: "right",
  });

  doc.setTextColor(20, 20, 20);

  let y = 34;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text(`Case: ${safeText(args.case_ref)}`, margin, y);

  y += 8;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text("Documento de abertura e identificação do atendimento.", margin, y);

  y += 10;

  const section = (title: string, startY: number, height: number) => {
    doc.setDrawColor(232, 232, 232);
    doc.setLineWidth(0.3);
    doc.rect(margin, startY, contentW, height);

    doc.setFillColor(245, 247, 250);
    doc.rect(margin, startY, contentW, 8, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    doc.text(title.toUpperCase(), margin + 3, startY + 5.6);

    doc.setTextColor(20, 20, 20);
    return startY + 12;
  };

  const field = (
    label: string,
    value: string,
    x: number,
    yy: number,
    maxWidth: number
  ) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    doc.setTextColor(70, 70, 70);
    doc.text(label, x, yy);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(15, 15, 15);

    const lines = doc.splitTextToSize(value, maxWidth);
    doc.text(lines, x, yy + 5);
    return yy + 5 + lines.length * 5.2;
  };

  const colGap = 8;
  const colW = (contentW - colGap) / 2;

  const identTop = y;
  let cy = section("Identificação", identTop, 66);

  let leftY = cy;
  leftY = field(
    "Número do Case",
    safeText(args.case_ref),
    margin + 3,
    leftY,
    colW - 6
  );
  leftY = field(
    "Data de Recebimento",
    safeText(args.data_recebimento),
    margin + 3,
    leftY + 2,
    colW - 6
  );
  leftY = field(
    "Tipo de Atendimento",
    safeText(args.tipo_atendimento),
    margin + 3,
    leftY + 2,
    colW - 6
  );

  let rightY = cy;
  rightY = field(
    "Nota Fiscal",
    safeText(args.nf),
    margin + colW + colGap + 3,
    rightY,
    colW - 6
  );
  rightY = field(
    "Prioridade",
    args.prioridade == null ? "—" : String(args.prioridade),
    margin + colW + colGap + 3,
    rightY + 2,
    colW - 6
  );

  y = identTop + 66 + 10;

  const envolTop = y;
  cy = section("Envolvidos", envolTop, 44);

  leftY = cy;
  leftY = field(
    "Empresa",
    safeText(args.empresa_cliente_nome),
    margin + 3,
    leftY,
    colW - 6
  );

  rightY = cy;
  rightY = field(
    "Solicitantes",
    safeText(args.solicitante_nome),
    margin + colW + colGap + 3,
    rightY,
    colW - 6
  );

  y = envolTop + 44 + 10;

  const equipTop = y;
  cy = section("Equipamento", equipTop, 44);

  cy = field(
    "Embarcação",
    safeText(args.embarcacao_nome),
    margin + 3,
    cy,
    contentW - 6
  );
  cy = field(
    "Nome do Equipamento",
    safeText(args.equipamento_nome),
    margin + 3,
    cy + 2,
    contentW - 6
  );

  y = equipTop + 44 + 10;

  const probTop = y;
  const desc = safeText(args.descricao_problema);
  const descLines = doc.splitTextToSize(desc, contentW - 6);

  const lineH = 5.2;
  const minBoxH = 34;
  const maxBoxH = Math.max(minBoxH, maxContentBottom - probTop);

  const neededTextH = descLines.length * lineH;
  const neededBoxH = 12 + neededTextH + 8;

  const probBoxH = Math.min(maxBoxH, Math.max(minBoxH, neededBoxH));
  const descY = section("Descrição do Problema", probTop, probBoxH);

  const usableTextH = probBoxH - 12 - 8;
  const maxLines = Math.max(1, Math.floor(usableTextH / lineH));
  const clipped = descLines.slice(0, maxLines);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(15, 15, 15);
  doc.text(clipped, margin + 3, descY);

  if (descLines.length > clipped.length) {
    doc.setFontSize(9);
    doc.setTextColor(120, 120, 120);
    doc.text("…(texto continua)", margin + 3, probTop + probBoxH - 6);
  }

  doc.setTextColor(120, 120, 120);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(
    "Logvi • Laboratório — Documento gerado automaticamente a partir do registro do case.",
    margin,
    footerY
  );
  doc.text(`Página 1/1`, pageW - margin, footerY, { align: "right" });

  return doc.output("blob") as Blob;
}

async function uploadPdfToOneDriveLabFolder(
  accessToken: string,
  caseRef: string,
  fileName: string,
  pdfBlob: Blob
) {
  const safeCaseRef = encodeURIComponent(caseRef);
  const safeFileName = encodeURIComponent(fileName);

  const path = `DEPARTAMENTOS/LABORATORIO/${safeCaseRef}/${safeFileName}`;
  const url = `https://graph.microsoft.com/v1.0/me/drive/root:/${path}:/content`;

  const res = await fetch(url, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/pdf",
    },
    body: pdfBlob,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `Falha ao enviar PDF ao OneDrive (HTTP ${res.status}). ${
        text || ""
      }`.trim()
    );
  }

  return res.json().catch(() => ({}));
}

const NoticeBanner: React.FC<{ notice: Notice; onClose?: () => void }> = ({
  notice,
  onClose,
}) => {
  let bg = "#e7f1ff";
  let color = "#135321";
  let border = "#b6d4fe";

  if (notice.type === "success") {
    bg = "#e6ffed";
    color = "#0f5132";
    border = "#badbcc";
  }
  if (notice.type === "error") {
    bg = "#ffecec";
    color = "#842029";
    border = "#f5c2c7";
  }
  if (notice.type === "notify") {
    bg = "#fff7e6";
    color = "#664d03";
    border = "#ffecb5";
  }

  return (
    <div
      role="alert"
      style={{
        margin: "12px 0",
        padding: "10px 12px",
        borderRadius: 8,
        background: bg,
        color,
        border: `1px solid ${border}`,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ flex: 1 }}>{notice.text}</span>
        {onClose && (
          <button
            type="button"
            aria-label="Fechar aviso"
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: "inherit",
              fontSize: 16,
              cursor: "pointer",
            }}
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
};

const RegistrarCase: React.FC = () => {
  const [caseNumber, setCaseNumber] = useState("");
  const [equipmentName, setEquipmentName] = useState("");
  const [problemDescription, setProblemDescription] = useState("");

  const [receivedDate, setReceivedDate] = useState<string>(() => todayBR());
  const [notaFiscal, setNotaFiscal] = useState<string>("N/I");
  const [tipoAtendimento, setTipoAtendimento] = useState<string>("MANUTENCAO");

  const [nomeEmpresa, setNomeEmpresa] = useState("");
  const [nomeEmbarcacao, setNomeEmbarcacao] = useState("");

  const [loadingEmpresas, setLoadingEmpresas] = useState(false);
  const [erroEmpresas, setErroEmpresas] = useState<string | null>(null);
  const [empresaOptions, setEmpresaOptions] = useState<EmpresaOpt[]>([]);
  const [empresaSelectedId, setEmpresaSelectedId] = useState<number | null>(
    null
  );
  const [empresasTemId, setEmpresasTemId] = useState<boolean>(false);

  const [loadingSolicitantes, setLoadingSolicitantes] = useState(false);
  const [erroSolicitantes, setErroSolicitantes] = useState<string | null>(null);
  const [solicitanteOptions, setSolicitanteOptions] = useState<
    SolicitanteOpt[]
  >([]);

  const [solicitantesSelected, setSolicitantesSelected] = useState<
    SelectedSolic[]
  >([]);

  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);

  const { instance, accounts } = useMsal();

  const empresaDropdown = useMemo(
    () => empresaOptions.map((e) => e.nome),
    [empresaOptions]
  );

  useEffect(() => {
    const found = empresaOptions.find((x) => x.nome === nomeEmpresa);
    setEmpresaSelectedId(found?.id ?? null);
  }, [nomeEmpresa, empresaOptions]);

  async function loadEmpresas() {
    setLoadingEmpresas(true);
    setErroEmpresas(null);

    try {
      const { res: resC, data: dataC } = await fetchJson(API_EMPRESAS_COMPLETO);

      if (resC.ok && dataC?.ok && Array.isArray(dataC?.data)) {
        const list: EmpresaOpt[] = (dataC.data as any[])
          .map((item) => {
            const nome = pickNomeFromObj(item);
            const id = pickIdFromObj(item);
            if (!nome) return null;
            return { nome: nome.toUpperCase(), id };
          })
          .filter(Boolean) as EmpresaOpt[];

        const temId = list.some((x) => x.id != null);

        if (list.length) {
          list.sort((a, b) => a.nome.localeCompare(b.nome));
          setEmpresaOptions(list);
          setEmpresasTemId(temId);

          if (!temId) {
            setErroEmpresas(
              "Empresas carregadas, mas sem IDs. Para registrar, é necessário empresa_cliente_id."
            );
          }
          return;
        }
      }

      const { res: resN, data: dataN } = await fetchJson(API_EMPRESAS_NOME);

      if (resN.ok && dataN?.ok && Array.isArray(dataN?.data)) {
        const list: EmpresaOpt[] = (dataN.data as string[])
          .filter((s) => typeof s === "string" && s.trim())
          .map((nome) => ({ nome: nome.trim().toUpperCase(), id: null }))
          .sort((a, b) => a.nome.localeCompare(b.nome));

        setEmpresaOptions(list);
        setEmpresasTemId(false);
        setErroEmpresas(
          "Lista carregada sem IDs. Para registrar, é necessário empresa_cliente_id. Use /geral/empresa-clientes-completo."
        );
        return;
      }

      setEmpresaOptions([]);
      setEmpresasTemId(false);
      setErroEmpresas("Não foi possível carregar a lista de empresas.");
    } catch (e: any) {
      setEmpresaOptions([]);
      setEmpresasTemId(false);
      setErroEmpresas(e?.message || "Falha ao carregar empresas.");
    } finally {
      setLoadingEmpresas(false);
    }
  }

  async function loadSolicitantes() {
    setLoadingSolicitantes(true);
    setErroSolicitantes(null);

    try {
      const { res, data, rawText } = await fetchJson(API_SOLICITANTES);

      console.log("[solicitantes] status =", res.status);
      console.log(
        "[solicitantes] headers =",
        Object.fromEntries(res.headers.entries())
      );
      console.log("[solicitantes] rawText =", rawText);
      console.log("[solicitantes] data =", data);

      if (res.ok && data?.ok && Array.isArray(data?.data)) {
        const list: SolicitanteOpt[] = (data.data as any[])
          .map((item) => {
            const nome = typeof item?.nome === "string" ? item.nome.trim() : "";
            const id = asNumber(item?.id);
            if (!nome || id == null) return null;
            return { nome: nome.toUpperCase(), id };
          })
          .filter(Boolean) as SolicitanteOpt[];

        list.sort((a, b) => a.nome.localeCompare(b.nome));
        setSolicitanteOptions(list);

        if (!list.length) {
          setErroSolicitantes("Nenhum solicitante encontrado.");
        }
        return;
      }

      const msg =
        data?.detalhe ||
        data?.erro ||
        data?.message ||
        data?.raw ||
        rawText ||
        `Não foi possível carregar a lista de solicitantes (HTTP ${res.status}).`;

      setSolicitanteOptions([]);
      setErroSolicitantes(msg);
    } catch (e: any) {
      console.error("[solicitantes] exception =", e);
      setSolicitanteOptions([]);
      setErroSolicitantes(e?.message || "Falha ao carregar solicitantes.");
    } finally {
      setLoadingSolicitantes(false);
    }
  }

  useEffect(() => {
    loadEmpresas();
    loadSolicitantes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function ensureAccessToken(): Promise<string> {
    const account =
      accounts[0] ||
      (await instance.loginPopup(loginRequestHavoxCases)).account;

    const tokenResp = await instance.acquireTokenSilent({
      ...loginRequestHavoxCases,
      account,
    });

    return tokenResp.accessToken;
  }

  async function handleSubmit() {
    setNotice(null);

    const caseRefLocal = sanitizeCaseRef(caseNumber);
    const equipLocal = equipmentName.trim();
    const descLocal = problemDescription.trim();
    const embarcacaoLocal = nomeEmbarcacao.trim();
    const tipoAtendimentoLocal = (tipoAtendimento ?? "").trim().toUpperCase();

    const nfLocal = (notaFiscal ?? "").trim();
    const nfToSend = nfLocal ? nfLocal : "N/I";

    if (!caseRefLocal) {
      setNotice({ type: "error", text: "Preencha o Número do Case." });
      return;
    }
    if (!tipoAtendimentoLocal) {
      setNotice({ type: "error", text: "Selecione o Tipo de Atendimento." });
      return;
    }
    if (!["MANUTENCAO", "DOCAGEM"].includes(tipoAtendimentoLocal)) {
      setNotice({
        type: "error",
        text: "Tipo de atendimento inválido.",
      });
      return;
    }
    if (!equipLocal) {
      setNotice({ type: "error", text: "Preencha o Nome do Equipamento." });
      return;
    }
    if (!descLocal) {
      setNotice({ type: "error", text: "Preencha a Descrição do Problema." });
      return;
    }
    if (!empresaSelectedId) {
      setNotice({
        type: "error",
        text:
          "Não foi possível obter o ID da empresa selecionada (empresa_cliente_id). " +
          "Garanta que a lista venha do endpoint /geral/empresa-clientes-completo (id+nome).",
      });
      return;
    }
    if (!solicitantesSelected.length) {
      setNotice({ type: "error", text: "Selecione pelo menos 1 solicitante." });
      return;
    }

    const dataReceb =
      receivedDate.trim().length === 10 ? receivedDate.trim() : null;

    const solicitanteIds = solicitantesSelected.map((s) => s.id);
    const solicitanteNome = solicitantesSelected.map((s) => s.label).join(", ");

    const payload: any = {
      case_ref: caseRefLocal,
      data_recebimento: dataReceb,
      nf: nfToSend,
      tipo_atendimento: tipoAtendimentoLocal,
      equipamento_nome: equipLocal,
      descricao_problema: descLocal,
      empresa_cliente_id: empresaSelectedId,

      solicitantes_ids: solicitanteIds,
      solicitante_id: solicitanteIds[0],

      prioridade: 1,

      empresa_cliente_nome: nomeEmpresa.trim() || null,
      embarcacao_nome: embarcacaoLocal || null,
      solicitante_nome: solicitanteNome || null,
    };

    setSubmitting(true);
    try {
      const res = await fetch(API_REGISTER, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data?.ok === false) {
        const msg =
          data?.erro ||
          (Array.isArray(data?.erros) && data.erros.join(" | ")) ||
          data?.message ||
          `Falha ao registrar case (HTTP ${res.status}).`;
        setNotice({ type: "error", text: msg });
        return;
      }

      const tracking = data?.data?.reparo?.tracking_code;

      if (ENABLE_ONEDRIVE_INTEGRATION) {
        try {
          const accessToken = await ensureAccessToken();
          const folder = await createCaseFolderInLab(accessToken, caseRefLocal);

          const pdfBlob = generateCasePdfBlob({
            case_ref: caseRefLocal,
            data_recebimento: dataReceb,
            nf: nfToSend,
            tipo_atendimento: tipoAtendimentoLocal,
            equipamento_nome: equipLocal,
            descricao_problema: descLocal,
            empresa_cliente_nome: payload.empresa_cliente_nome,
            embarcacao_nome: payload.embarcacao_nome,
            solicitante_nome: payload.solicitante_nome,
            prioridade: payload.prioridade,
          });

          const pdfName = `FICHA - ${caseRefLocal}.pdf`;

          await uploadPdfToOneDriveLabFolder(
            accessToken,
            caseRefLocal,
            pdfName,
            pdfBlob
          );

          setNotice({
            type: "success",
            text: tracking
              ? `Case registrado! Código Interno: ${tracking}. PDF salvo no OneDrive.`
              : "Case registrado! PDF salvo no OneDrive.",
          });
        } catch (e: any) {
          setNotice({
            type: "notify",
            text:
              (tracking
                ? `Case registrado! Código Interno: ${tracking}. `
                : "Case registrado. ") +
              "Falha no OneDrive: " +
              (e?.message || "erro desconhecido."),
          });
        }
      } else {
        setNotice({
          type: "success",
          text: tracking
            ? `Case registrado com sucesso! Código Interno: ${tracking}.`
            : "Case registrado com sucesso!",
        });
      }

      setCaseNumber("");
      setEquipmentName("");
      setProblemDescription("");
      setReceivedDate(todayBR());
      setNotaFiscal("N/I");
      setTipoAtendimento("MANUTENCAO");
      setNomeEmpresa("");
      setNomeEmbarcacao("");
      setEmpresaSelectedId(null);
      setSolicitantesSelected([]);
    } catch (err: any) {
      setNotice({
        type: "error",
        text: `Erro de rede: ${err?.message || "tente novamente."}`,
      });
    } finally {
      setSubmitting(false);
    }
  }

  const disableSubmit =
    submitting ||
    loadingEmpresas ||
    loadingSolicitantes ||
    !empresaSelectedId ||
    solicitantesSelected.length === 0 ||
    !tipoAtendimento;

  const solicitantesMultiOptions = useMemo(() => {
    return solicitanteOptions
      .filter((s) => s.id != null)
      .map((s) => ({ id: s.id as number, label: s.nome }));
  }, [solicitanteOptions]);

  return (
    <div>
      <TitleButtons title="Laboratório - Registrar Case" />

      {notice && (
        <NoticeBanner notice={notice} onClose={() => setNotice(null)} />
      )}

      <div>
        <TextField
          label="Número do Case"
          value={caseNumber}
          onChange={(v: string) => setCaseNumber(sanitizeCaseRef(v))}
          placeholder="Ex.: HVX-26-001-LAB"
          uppercase
        />

        <TextFieldRow
          left={{
            label: "Data de Recebimento",
            value: receivedDate,
            onChange: (v: string) => setReceivedDate(maskDateBR(v)),
            placeholder: "DD/MM/AAAA",
          }}
          right={{
            label: "Tipo de Atendimento",
            value: tipoAtendimento,
            onChange: (v: string) =>
              setTipoAtendimento((v ?? "").toUpperCase()),
            placeholder: "SELECIONE",
            options: TIPO_ATENDIMENTO_OPTIONS,
          }}
        />

        <TextField
          label="Nota Fiscal"
          value={notaFiscal}
          onChange={toUpper(setNotaFiscal)}
          placeholder="N/I"
          uppercase
        />

        <TextField
          label="Nome do Equipamento"
          value={equipmentName}
          onChange={toUpper(setEquipmentName)}
          placeholder="Ex.: RÁDIO VHF ICOM IC-M510"
          uppercase
        />

        <TextFieldRow
          left={{
            label: "Empresa",
            value: nomeEmpresa,
            onChange: toUpper(setNomeEmpresa),
            placeholder: loadingEmpresas
              ? "CARREGANDO EMPRESAS..."
              : erroEmpresas
              ? "FALHA AO CARREGAR. DIGITE MANUALMENTE."
              : "SELECIONE A EMPRESA",
            options: empresaDropdown,
          }}
          right={{
            label: "Embarcação",
            value: nomeEmbarcacao,
            onChange: toUpper(setNomeEmbarcacao),
            placeholder: "DIGITE A EMBARCAÇÃO",
          }}
        />

        <MultiSelectField
          label="Solicitantes"
          value={solicitantesSelected}
          onChange={setSolicitantesSelected}
          options={solicitantesMultiOptions}
          placeholder={
            loadingSolicitantes
              ? "CARREGANDO SOLICITANTES..."
              : erroSolicitantes
              ? "FALHA AO CARREGAR"
              : "SELECIONE OS SOLICITANTES"
          }
          disabled={loadingSolicitantes || !!erroSolicitantes}
          noResultsText="Sem solicitantes"
          uppercase
        />

        <TextField
          label="Descrição do Problema"
          value={problemDescription}
          onChange={toUpper(setProblemDescription)}
          placeholder="Ex.: NÃO TRANSMITE"
          uppercase
        />
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
        <Button
          text={disableSubmit ? "Preencha os campos..." : "Registrar"}
          onClick={handleSubmit}
          disabled={disableSubmit}
        />
      </div>

      {!empresasTemId && (
        <div style={{ marginTop: 10, opacity: 0.75, fontSize: 12 }}>
          Observação: para registrar, a lista de empresas precisa conter{" "}
          <b>ID</b> (empresa_cliente_id).
        </div>
      )}
    </div>
  );
};

export default RegistrarCase;
